import React, { useEffect } from 'react';
import styled from 'styled-components';
import { rooms, breakfast, getRoomText, getRoomById } from '../config/roomTexts';
import { useLanguage } from '../context/LanguageContext';

// Placeholder images - à remplacer par les vraies photos plus tard
const roomImages = {
  single: '/images/simple.jpg',
  double: '/images/double.jpg',
  design: '/images/design.jpg',
  triple: '/images/triple.jpg',
  family4: '/images/quadruple.jpg',
  family5: '/images/family.jpg',
};

const PageHeader = styled.div`
  height: 40vh;
  min-height: 300px;
  background-image: linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('/images/room.jpg');
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.white};
  margin-top: 70px; /* Pour laisser de l'espace pour le header fixe */
`;

const PageTitle = styled.h1`
  font-size: 3rem;
  text-align: center;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
  color: ${({ theme }) => theme.colors.white};
`;

const RoomsSection = styled.section`
  padding: ${({ theme }) => theme.spacing.xxl} 0;
`;

const RoomsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing.md};
`;

const RoomCard = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.xl};
  margin-bottom: ${({ theme }) => theme.spacing.xxl};
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: ${({ theme }) => theme.shadows.medium};
  overflow: hidden;
  
  &:nth-child(even) {
    direction: rtl;
    
    > * {
      direction: ltr;
    }
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    
    &:nth-child(even) {
      direction: ltr;
    }
  }
`;

const RoomImage = styled.div`
  height: 100%;
  min-height: 300px;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
`;

const RoomContent = styled.div`
  padding: ${({ theme }) => theme.spacing.xl};
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const RoomTitle = styled.h2`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.8rem;
`;

const RoomDescription = styled.p`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.textLight};
  line-height: 1.6;
`;

const RoomFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.sm};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const RoomFeature = styled.li`
  padding: ${({ theme }) => theme.spacing.xs} 0;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: ${({ theme }) => theme.spacing.sm};
    color: ${({ theme }) => theme.colors.accent};
  }
`;

const RoomPrice = styled.div`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.5rem;
  margin-top: auto;
  padding-top: ${({ theme }) => theme.spacing.md};
  border-top: 1px solid ${({ theme }) => theme.colors.light};
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  small {
    font-size: 0.9rem;
    font-weight: 400;
    color: ${({ theme }) => theme.colors.textLight};
    display: block;
  }
`;

const BreakfastSection = styled.section`
  padding: ${({ theme }) => theme.spacing.xl} 0;
  background-color: ${({ theme }) => theme.colors.light};
  margin-bottom: ${({ theme }) => theme.spacing.xxl};
`;

const BreakfastContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing.md};
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.xl};
  align-items: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const BreakfastImage = styled.div`
  height: 300px;
  background-image: url('/images/breakfast.jpg');
  background-size: cover;
  background-position: center;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: ${({ theme }) => theme.shadows.medium};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    order: 1;
  }
`;

const BreakfastContent = styled.div`
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    order: 2;
    margin-bottom: ${({ theme }) => theme.spacing.lg};
  }
`;

const BreakfastTitle = styled.h2`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.8rem;
`;

const BreakfastDescription = styled.p`
  color: ${({ theme }) => theme.colors.textLight};
  line-height: 1.6;
  font-size: 1.1rem;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const BreakfastPrice = styled.p`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 700;
  font-size: 1.1rem;
  margin-top: ${({ theme }) => theme.spacing.md};
`;

const Rooms = () => {
  const { language } = useLanguage();
  
  // Obtenir les traductions du petit-déjeuner
  const breakfastText = getRoomText(language, 'breakfast') || breakfast;

  // Effet pour gérer le défilement automatique vers l'ancre
  useEffect(() => {
    // Récupérer l'ancre de l'URL (sans le #)
    const hash = window.location.hash.substring(1);
    
    if (hash) {
      // Attendre un peu que le DOM soit complètement chargé
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          // Calculer la position de défilement en tenant compte du header fixe (70px)
          const headerOffset = 80; // Un peu plus que la hauteur du header pour avoir de l'espace
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          
          // Faire défiler la page
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, []);
  
  return (
    <>
      <PageHeader>
        <PageTitle>{getRoomText(language, 'pageTitle')}</PageTitle>
      </PageHeader>
      
      <RoomsSection>
        <RoomsContainer>
          {rooms.map((room) => {
            const translatedRoom = getRoomById(language, room.id);
            return (
              <RoomCard key={room.id} id={room.id}>
                <RoomImage image={roomImages[room.id]} />
                <RoomContent>
                  <RoomTitle>{translatedRoom?.title || room.title}</RoomTitle>
                  <RoomDescription>{translatedRoom?.description || room.description}</RoomDescription>
                  
                  <RoomFeatures>
                    {(translatedRoom?.features || room.features).map((feature, index) => (
                      <RoomFeature key={index}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                          <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
                        </svg>
                        {feature}
                      </RoomFeature>
                    ))}
                  </RoomFeatures>
                  
                  <RoomPrice>
                    <div>
                      {getRoomText(language, 'pricePrefix')} {room.price}€
                      <small>{getRoomText(language, 'taxInfo').replace('{taxe}', room.taxe)}</small>
                    </div>
                  </RoomPrice>
                </RoomContent>
              </RoomCard>
            );
          })}
        </RoomsContainer>
      </RoomsSection>
      
      <BreakfastSection>
        <BreakfastContainer>
          <BreakfastContent>
            <BreakfastTitle>{breakfastText.title}</BreakfastTitle>
            <BreakfastDescription>{breakfastText.description}</BreakfastDescription>
            <BreakfastPrice>{breakfastText.priceText}</BreakfastPrice>
          </BreakfastContent>
          <BreakfastImage />
        </BreakfastContainer>
      </BreakfastSection>
    </>
  );
};

export default Rooms;
